import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { SubscriptionStats } from "@/components/subscriptions/SubscriptionStats";
import { SubscriptionFilters } from "@/components/subscriptions/SubscriptionFilters";
import { SubscriptionTable } from "@/components/subscriptions/SubscriptionTable";
import { UnifiedPaymentDialog } from "@/components/subscriptions/UnifiedPaymentDialog";
import { useRealtimeSubscription } from "@/hooks/use-realtime";
import { useCurrency } from "@/hooks/use-currency";

const Subscriptions = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [currencyFilter, setCurrencyFilter] = useState("all");
  const { convertAmount, currency, formatAmount } = useCurrency();

  const { data: subscriptions, isLoading: loadingSubscriptions } = useQuery({
    queryKey: ["subscriptions"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("No user found");

      // RLS policies handle filtering - agency members see all, users see only theirs
      const { data, error } = await supabase
        .from("subscriptions")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data;
    },
  });
  
  const { data: invoices, isLoading: loadingInvoices } = useQuery({
    queryKey: ["payment-invoices"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("No user found");

      // RLS policies handle filtering
      const { data, error } = await supabase
        .from("invoices")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data;
    },
  });

  // Enable realtime updates
  useRealtimeSubscription("subscriptions", "subscriptions");
  useRealtimeSubscription("invoices", "payment-invoices");
  
  const isLoading = loadingSubscriptions || loadingInvoices;

  const filteredSubscriptions = subscriptions?.filter((sub) => {
    const matchesSearch =
      sub.plan_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      sub.transaction_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      sub.client_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      sub.client_email?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus =
      statusFilter === "all" || sub.payment_status === statusFilter;
    const matchesCurrency =
      currencyFilter === "all" || sub.currency === currencyFilter;
    return matchesSearch && matchesStatus && matchesCurrency;
  });

  // Calculate total revenue - exclude invoice-type subscriptions to avoid double counting
  const subscriptionRevenue = subscriptions
    ?.filter((sub) => sub.payment_type !== 'invoice')
    .reduce((acc, sub) => {
      const sourceCurrency = (sub.currency as any) || "USD";
      return acc + convertAmount(Number(sub.price), sourceCurrency, currency);
    }, 0) || 0;
  
  const invoiceRevenue = invoices?.filter(inv => inv.status !== 'draft' && inv.status !== 'cancelled')
    .reduce((acc, inv) => {
      const sourceCurrency = (inv.currency as any) || "USD";
      return acc + convertAmount(Number(inv.total_amount), sourceCurrency, currency);
    }, 0) || 0;
  
  const totalRevenue = subscriptionRevenue + invoiceRevenue;
  
  // Count only non-invoice subscriptions as they're tracked separately
  const completedSubscriptions = subscriptions?.filter(
    (sub) => sub.payment_status === "completed" && sub.payment_type !== 'invoice'
  ).length || 0;
  
  const paidInvoices = invoices?.filter(
    (inv) => inv.status === "paid"
  ).length || 0;
  
  const completedPayments = completedSubscriptions + paidInvoices;
  
  const pendingSubscriptions = subscriptions?.filter(
    (sub) => sub.payment_status === "pending"
  ).length || 0;
  
  const pendingInvoices = invoices?.filter(
    (inv) => inv.status === "sent" || inv.status === "overdue"
  ).length || 0;
  
  const pendingPayments = pendingSubscriptions + pendingInvoices;

  return (
    <div className="animate-fade-in space-y-4 sm:space-y-6">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-primary to-purple-600 bg-clip-text text-transparent">
            Payments & Subscriptions
          </h1>
          <p className="text-sm text-muted-foreground mt-2">
            Manage all your client payments and subscriptions
          </p>
        </div>
        <UnifiedPaymentDialog />
      </div>

      <SubscriptionStats
        totalRevenue={totalRevenue}
        completedSubscriptions={completedPayments}
        pendingSubscriptions={pendingPayments}
      />

      <div className="space-y-3 sm:space-y-4">
        <SubscriptionFilters
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          setStatusFilter={setStatusFilter}
          currencyFilter={currencyFilter}
          setCurrencyFilter={setCurrencyFilter}
        />

        {isLoading ? (
          <div className="text-center py-12">
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-current border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]" />
            <p className="mt-4 text-sm text-muted-foreground">Loading payments...</p>
          </div>
        ) : filteredSubscriptions && filteredSubscriptions.length > 0 ? (
          <SubscriptionTable subscriptions={filteredSubscriptions} />
        ) : (
          <div className="text-center py-12 bg-muted/50 rounded-lg">
            <p className="text-base sm:text-lg text-muted-foreground">No payments found</p>
            <p className="text-xs sm:text-sm text-muted-foreground mt-2">
              Create your first payment to get started
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Subscriptions;